import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ChevronLeft, Mail, ExternalLink } from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';

// Contact Us Page
export const ContactUs = ({ onBack }) => {
    const handleEmailClick = () => {
        window.location.href = 'mailto:support@eskrybe.com?subject=Support%20Request';
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-3xl mx-auto px-6 py-12">
                <Button variant="ghost" onClick={onBack} className="mb-6" data-testid="back-btn">
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Back
                </Button>

                <div className="text-center mb-12">
                    <h1 className="font-serif text-4xl font-semibold mb-4">Contact Us</h1>
                    <p className="text-muted-foreground text-lg">
                        We're here to help you on your writing journey.
                    </p>
                </div>

                <Card className="mb-8">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Mail className="h-5 w-5 text-accent" />
                            Email Support
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-muted-foreground mb-4">
                            For general inquiries, technical support, billing questions, or partnership opportunities, 
                            please reach out to our support team.
                        </p>
                        <Button onClick={handleEmailClick} className="bg-accent hover:bg-accent/90 text-white" data-testid="email-support-btn">
                            <Mail className="h-4 w-4 mr-2" />
                            Email support@eskrybe.com
                        </Button>
                    </CardContent>
                </Card>

                <div className="grid md:grid-cols-2 gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Response Time</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-muted-foreground text-sm">
                                We typically respond within 24-48 hours during business days. 
                                Priority support is available for Author and Studio tier subscribers.
                            </p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Before You Contact Us</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-muted-foreground text-sm">
                                Check our Academy section for tutorials and FAQs that may answer your questions quickly.
                            </p>
                        </CardContent>
                    </Card>
                </div>

                <div className="mt-12 text-center text-sm text-muted-foreground">
                    <p>eSkrybe, LLC</p>
                    <p>A Wyoming Limited Liability Company</p>
                    <p className="mt-2">© {new Date().getFullYear()} eSkrybe, LLC. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
};

// Privacy Policy Page - Scrivener-style language adapted for eSkrybe
export const PrivacyPolicy = ({ onBack }) => {
    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-4xl mx-auto px-6 py-12">
                <Button variant="ghost" onClick={onBack} className="mb-6" data-testid="back-btn">
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Back
                </Button>

                <h1 className="font-serif text-4xl font-semibold mb-2">Privacy Policy</h1>
                <p className="text-muted-foreground mb-8">Last updated: December 15, 2025</p>

                <ScrollArea className="h-[70vh]">
                    <div className="prose prose-sm dark:prose-invert max-w-none space-y-6">
                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Privacy</h2>
                            <p className="text-muted-foreground">
                                eSkrybe, LLC respects your privacy. The only data we collect is necessary for the features of our 
                                software and website to run properly. Wherever we can, we provide the ability to opt-out of features 
                                that ask for personal information. The information below provides an overview of the sorts of data 
                                our software and website uses.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Who We Are</h2>
                            <p className="text-muted-foreground">
                                We are eSkrybe, LLC, a Wyoming Limited Liability Company, United States of America. 
                                You may contact us at support@eskrybe.com.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Billing Information</h2>
                            <p className="text-muted-foreground">
                                When you purchase a subscription or credits directly from us, your order information is processed 
                                by our third-party payment processor, Stripe. We do not store any of your purchasing information 
                                on our web server or within our software, and Stripe does not store your payment details once the 
                                transaction has completed. What will be stored is your general location and email address, provided 
                                when filling out the purchase form. Your email address will be used for verification purposes, so 
                                that we can retrieve your account information should you ever need assistance.
                            </p>
                            <p className="text-muted-foreground mt-2">
                                View <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Stripe's privacy policy</a>.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Content Data</h2>
                            <p className="text-muted-foreground">
                                Your creative works—including books, chapters, scenes, character profiles, worldbuilding notes, 
                                and plot outlines—are stored securely on our servers to provide the Service to you. We do not 
                                access, read, or use your content for any purpose other than providing and improving our Service. 
                                AI-generated content (text assistance and illustrations) is processed through third-party AI 
                                providers (OpenAI, fal.ai) using your prompts; we do not retain copies of AI interactions beyond 
                                what is necessary for immediate service delivery.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Internet Access</h2>
                            <p className="text-muted-foreground">
                                eSkrybe is a web-based application that requires an internet connection to function. Your browser 
                                connects to our servers (hosted securely) to load the application and synchronize your data. 
                                We use secure HTTPS connections for all data transmission.
                            </p>
                            <p className="text-muted-foreground mt-2">
                                If you make use of any external research features or integrations, eSkrybe may need to contact 
                                external websites on your behalf. You are responsible for maintaining your privacy and security 
                                when using these facilities.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Cookies</h2>
                            <p className="text-muted-foreground mb-3">
                                A cookie is a small piece of data that a website stores on your computer to improve the browsing 
                                experience by remembering your preferences.
                            </p>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                                <li>Our website uses cookies to remember preferences such as your theme (light/dark mode) and editor settings.</li>
                                <li>Authentication tokens are stored to keep you logged in securely.</li>
                                <li>Analytics cookies may be used to track usage of our site to improve the Service.</li>
                                <li>Payment processing through Stripe will use cookies to track your session as you proceed through checkout.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Support and Contacting Us</h2>
                            <p className="text-muted-foreground">
                                When you email us or use our contact form, your email and email address will stay in our support 
                                system, along with anything else you send, which enables us to keep track of the problem and refer 
                                to it should you need related help in the future. You can email us at any time at support@eskrybe.com 
                                to ask us to delete your information from our support system.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Data Protection</h2>
                            <p className="text-muted-foreground">
                                We hate spam as much as anyone else, and we don't share any data we hold on our users with anyone else.
                            </p>
                            <p className="text-muted-foreground mt-2">
                                You have a right to access any data we hold about you. Please contact support@eskrybe.com with 
                                your request, providing your name and the email address you registered under.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Data Retention</h2>
                            <p className="text-muted-foreground">
                                We retain your personal information for as long as your account is active or as needed to provide 
                                our Service. You may request deletion of your account and associated data at any time by contacting 
                                support@eskrybe.com. Certain information may be retained as required by law or for legitimate 
                                business purposes.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Your Rights</h2>
                            <p className="text-muted-foreground mb-2">Depending on your location, you may have the right to:</p>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                                <li>Access your personal data</li>
                                <li>Correct inaccurate data</li>
                                <li>Request deletion of your data</li>
                                <li>Object to or restrict processing</li>
                                <li>Data portability</li>
                                <li>Withdraw consent</li>
                            </ul>
                            <p className="text-muted-foreground mt-2">
                                To exercise these rights, contact us at support@eskrybe.com.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Security</h2>
                            <p className="text-muted-foreground">
                                We implement appropriate technical and organizational measures to protect your personal information, 
                                including encryption, secure servers, and access controls. However, no method of transmission over 
                                the Internet is 100% secure.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Children's Privacy</h2>
                            <p className="text-muted-foreground">
                                Our Service is not intended for children under 13 (or 16 in certain jurisdictions). We do not 
                                knowingly collect personal information from children. If you believe we have collected information 
                                from a child, please contact us immediately.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Changes to This Policy</h2>
                            <p className="text-muted-foreground">
                                We may update this Privacy Policy from time to time. We will notify you of any material changes 
                                by posting the new policy on this page and updating the "Last updated" date.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Contact Us</h2>
                            <p className="text-muted-foreground">
                                For questions about this Privacy Policy or our data practices, contact us at:<br />
                                Email: support@eskrybe.com<br />
                                eSkrybe, LLC<br />
                                A Wyoming Limited Liability Company
                            </p>
                        </section>
                    </div>
                </ScrollArea>
            </div>
        </div>
    );
};

// Terms of Use / End User License Agreement - Scrivener-style language adapted for eSkrybe
export const TermsOfUse = ({ onBack }) => {
    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-4xl mx-auto px-6 py-12">
                <Button variant="ghost" onClick={onBack} className="mb-6" data-testid="back-btn">
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Back
                </Button>

                <h1 className="font-serif text-4xl font-semibold mb-2">Terms of Use & End User License Agreement</h1>
                <p className="text-muted-foreground mb-8">Last updated: December 15, 2025</p>

                <ScrollArea className="h-[70vh]">
                    <div className="prose prose-sm dark:prose-invert max-w-none space-y-6">
                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">1. Grant of License</h2>
                            <p className="text-muted-foreground">
                                eSkrybe, LLC ("Licensor") grants you ("Licensee") a non-exclusive, non-transferable license to 
                                access and use the eSkrybe web-based writing platform ("Service") subject to the terms and 
                                conditions of this Agreement. This license permits you to use the Service for your personal 
                                or professional writing projects on any device with internet access. The license is granted to 
                                you individually; you may not share your account credentials with others.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">2. Description of Service</h2>
                            <p className="text-muted-foreground">
                                eSkrybe is a web-based writing platform that provides tools for authors to write, organize, 
                                and publish books. Our Service includes AI-powered writing assistance, illustration generation, 
                                book organization tools, professional formatting, and export capabilities.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">3. Restrictions</h2>
                            <p className="text-muted-foreground mb-2">You may not:</p>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                                <li>Disassemble, decompile, reverse engineer, modify, or create derivative works of the Service</li>
                                <li>Use the Service for any unlawful purpose or in violation of any applicable laws</li>
                                <li>Attempt to gain unauthorized access to the Service or its related systems</li>
                                <li>Share, sell, lease, sublicense, or otherwise transfer your account to any third party</li>
                                <li>Use automated scripts, bots, or other tools to access or interact with the Service</li>
                                <li>Remove, alter, or obscure any proprietary notices or labels on the Service</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">4. Ownership and Intellectual Property</h2>
                            <h3 className="font-medium mt-4 mb-2">4.1 Your Content</h3>
                            <p className="text-muted-foreground">
                                You retain all ownership rights to the content you create using our Service, including books, 
                                chapters, characters, and other creative works. We claim no intellectual property rights over 
                                your content. By using the Service, you grant us a limited license to store, process, and 
                                display your content solely for the purpose of providing the Service to you.
                            </p>
                            
                            <h3 className="font-medium mt-4 mb-2">4.2 Our Software</h3>
                            <p className="text-muted-foreground">
                                The Service, including its original content, features, functionality, software code, and design, 
                                is owned by eSkrybe, LLC and is protected by copyright, trademark, and other intellectual 
                                property laws of the United States and international treaties. Our trademarks and trade dress 
                                may not be used without our prior written permission.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">5. Subscriptions and Payments</h2>
                            <h3 className="font-medium mt-4 mb-2">5.1 Billing</h3>
                            <p className="text-muted-foreground">
                                Paid subscriptions are billed in advance on a monthly or annual basis. Prices are subject to 
                                change with notice. All payments are processed securely through Stripe, our third-party 
                                payment processor.
                            </p>

                            <h3 className="font-medium mt-4 mb-2">5.2 AI Credits</h3>
                            <p className="text-muted-foreground">
                                AI features (writing assistance, illustrations, etc.) consume credits. Credits included with 
                                your subscription reset each billing period. Purchased credits do not expire while your account 
                                is active. Unused purchased credits are non-refundable.
                            </p>

                            <h3 className="font-medium mt-4 mb-2">5.3 Cancellation</h3>
                            <p className="text-muted-foreground">
                                You may cancel your subscription at any time. Cancellation takes effect at the end of the 
                                current billing period. No refunds are provided for partial periods.
                            </p>
                        </section>

                        <section className="bg-muted/30 p-4 rounded-lg border border-border">
                            <h2 className="font-serif text-xl font-semibold mb-3">6. Refund Policy</h2>
                            <p className="text-muted-foreground">
                                <strong>Due to the nature of our affiliate program, refunds are limited to a maximum of 50% 
                                of the original payment amount.</strong> This limitation exists because affiliate commissions 
                                are paid upon initial purchase and cannot be fully recovered. Refund requests must be made 
                                within 14 days of purchase. To request a refund, contact support@eskrybe.com with your 
                                account email and reason for the request. Refunds are issued at our sole discretion and 
                                will be processed to the original payment method within 5-10 business days.
                            </p>
                            <p className="text-muted-foreground mt-2">
                                <strong>Credit purchases are non-refundable</strong> once credits have been used. Unused credit 
                                balances may be eligible for partial refund (up to 50%) at our discretion.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">7. AI-Generated Content</h2>
                            <p className="text-muted-foreground">
                                Our Service uses artificial intelligence to assist with writing and generate illustrations. 
                                You acknowledge that:
                            </p>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                                <li>AI-generated content may require review and editing</li>
                                <li>You are responsible for the final content you publish</li>
                                <li>AI suggestions are tools to assist, not replace, your creative judgment</li>
                                <li>You own the rights to AI-generated illustrations created through your prompts</li>
                                <li>AI features are provided "as is" and may occasionally produce unexpected results</li>
                            </ul>
                        </section>

                        <section className="bg-amber-50 dark:bg-amber-950/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                            <h2 className="font-serif text-xl font-semibold mb-3 text-amber-800 dark:text-amber-200">8. Data Responsibility & Backup</h2>
                            <p className="text-muted-foreground mb-3">
                                <strong>IMPORTANT: You are responsible for maintaining backups of your work.</strong>
                            </p>
                            <p className="text-muted-foreground mb-3">
                                While we employ industry-standard security measures and regularly back up our systems, 
                                <strong> eSkrybe, LLC is not liable for any loss of data</strong>, including but not limited to 
                                manuscripts, chapters, notes, illustrations, or any other content you create or store on our platform. 
                                Data loss may occur due to technical failures, security incidents, account termination, or other 
                                unforeseen circumstances.
                            </p>
                            <p className="text-muted-foreground mb-3">
                                <strong>We strongly recommend that you:</strong>
                            </p>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-3">
                                <li>Regularly download and export your manuscripts using our export features</li>
                                <li>Maintain local copies of all important work on your own devices</li>
                                <li>Use a third-party cloud storage service for additional backup protection</li>
                                <li>Export your work before canceling your subscription</li>
                            </ul>
                            <p className="text-muted-foreground">
                                <strong>Recommended backup solution:</strong> We recommend using a secure cloud storage service 
                                such as <a href="https://www.pcloud.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">pCloud</a> for 
                                automatic, encrypted backups of your downloaded manuscripts. pCloud offers a free tier and 
                                lifetime storage options for writers who want peace of mind.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">9. Warranty Disclaimer</h2>
                            <p className="text-muted-foreground">
                                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
                                EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, 
                                FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE 
                                SERVICE WILL BE UNINTERRUPTED, SECURE, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL 
                                COMPONENTS. THE LICENSOR DOES NOT GUARANTEE THE ACCURACY, COMPLETENESS, OR USEFULNESS OF 
                                ANY AI-GENERATED CONTENT.
                            </p>
                            <p className="text-muted-foreground mt-2">
                                A trial or free tier is available so that you may evaluate the Service before purchasing a 
                                paid subscription. It is your responsibility to satisfy yourself that the Service is suitable 
                                for your needs before purchase.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">9. Limitation of Liability</h2>
                            <p className="text-muted-foreground">
                                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL ESKRYBE, LLC, ITS 
                                OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, 
                                DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR IN CONNECTION WITH YOUR 
                                ACCESS TO OR USE OF (OR INABILITY TO ACCESS OR USE) THE SERVICE.
                            </p>
                            <p className="text-muted-foreground mt-2">
                                OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THIS AGREEMENT OR 
                                THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO US IN THE TWELVE (12) 
                                MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100).
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">10. Indemnification</h2>
                            <p className="text-muted-foreground">
                                You agree to indemnify, defend, and hold harmless eSkrybe, LLC and its officers, directors, 
                                employees, agents, and affiliates from and against any and all claims, damages, obligations, 
                                losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from: 
                                (a) your use of the Service; (b) your violation of these Terms; (c) your violation of any 
                                third-party right, including any intellectual property or privacy right; or (d) any claim 
                                that your content caused damage to a third party.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">11. Termination</h2>
                            <p className="text-muted-foreground">
                                Either party may terminate this Agreement at any time. We may terminate or suspend your 
                                account immediately, without prior notice or liability, for any reason, including without 
                                limitation if you breach these Terms. Upon termination, your right to use the Service will 
                                immediately cease. You may export your content before termination using the export features 
                                provided. After termination, we may delete your account and content after a reasonable 
                                retention period (typically 30 days).
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">12. Updates to the Service</h2>
                            <p className="text-muted-foreground">
                                You are entitled to use the current version of the Service as provided. eSkrybe, LLC may 
                                release updates, new features, or modifications to the Service at its discretion. We are 
                                under no obligation to provide updates or maintain backward compatibility. Major feature 
                                changes will be communicated through the Service or via email.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">13. Governing Law and Disputes</h2>
                            <p className="text-muted-foreground">
                                This Agreement shall be governed by and construed in accordance with the laws of the State 
                                of Wyoming, United States of America, without regard to its conflict of law provisions. Any 
                                dispute arising out of or relating to this Agreement or the Service shall be resolved through 
                                binding arbitration administered by the American Arbitration Association in accordance with 
                                its Commercial Arbitration Rules, except where prohibited by applicable law. The arbitration 
                                shall take place in Wyoming, USA, and the language of arbitration shall be English.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">14. Severability</h2>
                            <p className="text-muted-foreground">
                                If any provision of this Agreement is held to be invalid, illegal, or unenforceable, the 
                                remaining provisions shall continue in full force and effect.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">15. Entire Agreement</h2>
                            <p className="text-muted-foreground">
                                This Agreement, together with our Privacy Policy and any other policies referenced herein, 
                                constitutes the entire agreement between you and eSkrybe, LLC regarding the Service and 
                                supersedes all prior agreements and understandings, whether written or oral.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">16. Changes to Terms</h2>
                            <p className="text-muted-foreground">
                                We reserve the right to modify these Terms at any time. We will provide notice of material 
                                changes through the Service or by email. Your continued use of the Service after changes 
                                constitutes acceptance of the revised Terms.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">17. Contact</h2>
                            <p className="text-muted-foreground">
                                For questions about these Terms, contact us at:<br />
                                Email: support@eskrybe.com<br /><br />
                                eSkrybe, LLC<br />
                                A Wyoming Limited Liability Company<br />
                                United States of America
                            </p>
                        </section>
                    </div>
                </ScrollArea>
            </div>
        </div>
    );
};

// Cookie Notice Page - Scrivener-style language adapted for eSkrybe
export const CookieNotice = ({ onBack }) => {
    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-4xl mx-auto px-6 py-12">
                <Button variant="ghost" onClick={onBack} className="mb-6" data-testid="back-btn">
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Back
                </Button>

                <h1 className="font-serif text-4xl font-semibold mb-2">Cookie Notice</h1>
                <p className="text-muted-foreground mb-8">Last updated: December 15, 2025</p>

                <ScrollArea className="h-[70vh]">
                    <div className="prose prose-sm dark:prose-invert max-w-none space-y-6">
                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">What Are Cookies?</h2>
                            <p className="text-muted-foreground">
                                A cookie is a small piece of data that a website stores on your computer to improve the 
                                browsing experience by remembering your preferences. Cookies are widely used to make websites 
                                work more efficiently and to provide information to the owners of the site.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">How eSkrybe Uses Cookies</h2>
                            <p className="text-muted-foreground mb-3">eSkrybe uses cookies for the following purposes:</p>
                            
                            <h3 className="font-medium mt-4 mb-2">Essential Cookies (Required)</h3>
                            <p className="text-muted-foreground">
                                These cookies are necessary for the Service to function properly. They enable core functionality 
                                such as authentication, security, and session management. You cannot opt out of these cookies 
                                as they are essential to use eSkrybe.
                            </p>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                                <li>Authentication tokens to keep you securely logged in</li>
                                <li>Session management for your writing workspace</li>
                                <li>Security cookies to prevent unauthorized access and fraud</li>
                                <li>Load balancing to ensure reliable service performance</li>
                            </ul>

                            <h3 className="font-medium mt-4 mb-2">Preference Cookies</h3>
                            <p className="text-muted-foreground">
                                These cookies remember your preferences and settings to enhance your experience.
                            </p>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                                <li>Theme preference (light/dark mode)</li>
                                <li>Editor settings such as line spacing and font preferences</li>
                                <li>Sidebar and panel states</li>
                                <li>Last accessed book or chapter</li>
                            </ul>

                            <h3 className="font-medium mt-4 mb-2">Analytics Cookies</h3>
                            <p className="text-muted-foreground">
                                These cookies help us understand how visitors interact with our Service by collecting and 
                                reporting information. This data is used to improve the Service and user experience.
                            </p>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                                <li>Pages visited and time spent on each page</li>
                                <li>Features used and user flows</li>
                                <li>Error tracking to identify and fix issues</li>
                                <li>Performance monitoring</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Third-Party Cookies</h2>
                            <p className="text-muted-foreground mb-3">
                                Some cookies are placed by third-party services that appear on our pages:
                            </p>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                                <li>
                                    <strong>Stripe:</strong> For secure payment processing. When you proceed through checkout, 
                                    Stripe will use cookies to track your session and ensure secure transactions. 
                                    <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline ml-1">View Stripe's privacy policy</a>
                                </li>
                                <li>
                                    <strong>Analytics providers:</strong> To help us understand usage patterns and improve the Service
                                </li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Managing Cookies</h2>
                            <p className="text-muted-foreground mb-3">
                                Most web browsers allow you to control cookies through their settings. You can:
                            </p>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                                <li>View what cookies are stored on your device</li>
                                <li>Delete all or specific cookies</li>
                                <li>Block all or certain cookies</li>
                                <li>Set preferences for certain websites</li>
                            </ul>
                            <p className="text-muted-foreground mt-3 p-3 bg-muted/50 rounded-lg">
                                <strong>Note:</strong> Blocking essential cookies will prevent you from using eSkrybe, as 
                                authentication and session management require these cookies to function. Blocking preference 
                                cookies may result in a degraded user experience.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Cookie Retention</h2>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                                <li><strong>Session cookies:</strong> Deleted when you close your browser</li>
                                <li><strong>Authentication cookies:</strong> Expire after 7 days of inactivity for security</li>
                                <li><strong>Preference cookies:</strong> Persist until you clear them or change settings</li>
                                <li><strong>Analytics cookies:</strong> Typically expire within 1-2 years</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Updates to This Notice</h2>
                            <p className="text-muted-foreground">
                                We may update this Cookie Notice from time to time. Any changes will be posted on this page 
                                with an updated revision date. We encourage you to review this notice periodically.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Contact Us</h2>
                            <p className="text-muted-foreground">
                                If you have questions about our use of cookies, please contact us at:<br />
                                Email: support@eskrybe.com<br /><br />
                                eSkrybe, LLC<br />
                                A Wyoming Limited Liability Company
                            </p>
                        </section>
                    </div>
                </ScrollArea>
            </div>
        </div>
    );
};

// Affiliate Program Page
export const AffiliateProgram = ({ onBack }) => {
    const handleApplyClick = () => {
        window.location.href = 'mailto:support@eskrybe.com?subject=Affiliate%20Program%20Application&body=Hi%20eSkrybe%20Team%2C%0A%0AI%27m%20interested%20in%20joining%20your%20affiliate%20program.%0A%0AName%3A%20%0AWebsite%2FSocial%20Media%3A%20%0AAudience%20Size%3A%20%0AHow%20I%20plan%20to%20promote%20eSkrybe%3A%20%0A%0AThank%20you!';
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-4xl mx-auto px-6 py-12">
                <Button variant="ghost" onClick={onBack} className="mb-6" data-testid="back-btn">
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Back
                </Button>

                <div className="text-center mb-12">
                    <h1 className="font-serif text-4xl font-semibold mb-4">Affiliate Program</h1>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                        Earn money by helping writers discover eSkrybe. Join our affiliate program and 
                        receive commissions for every new subscriber you refer.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mb-12">
                    <Card className="text-center">
                        <CardHeader>
                            <CardTitle className="text-3xl font-bold text-accent">30%</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-muted-foreground">Commission on first payment</p>
                        </CardContent>
                    </Card>
                    <Card className="text-center">
                        <CardHeader>
                            <CardTitle className="text-3xl font-bold text-accent">15%</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-muted-foreground">Recurring commission for 12 months</p>
                        </CardContent>
                    </Card>
                    <Card className="text-center">
                        <CardHeader>
                            <CardTitle className="text-3xl font-bold text-accent">60 Days</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-muted-foreground">Cookie duration for attribution</p>
                        </CardContent>
                    </Card>
                </div>

                <ScrollArea className="h-[50vh]">
                    <div className="prose prose-sm dark:prose-invert max-w-none space-y-6">
                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Program Overview</h2>
                            <p className="text-muted-foreground">
                                The eSkrybe Affiliate Program is designed for writers, bloggers, writing coaches, 
                                book reviewers, and anyone passionate about helping authors succeed. When you refer 
                                someone to eSkrybe and they become a paying subscriber, you earn a commission.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Commission Structure</h2>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                                <li><strong>First Payment:</strong> 30% commission on the initial subscription payment</li>
                                <li><strong>Recurring:</strong> 15% commission on all payments for the first 12 months</li>
                                <li><strong>Credit Purchases:</strong> 10% commission on credit pack purchases by referred users</li>
                            </ul>
                            <p className="text-muted-foreground mt-2">
                                Example: If you refer someone who subscribes to the Author plan ($29/month), you earn 
                                $8.70 on the first payment, then $4.35/month for the next 11 months.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">How It Works</h2>
                            <ol className="list-decimal pl-6 text-muted-foreground space-y-2">
                                <li><strong>Apply:</strong> Submit your application and tell us about your audience</li>
                                <li><strong>Get Approved:</strong> We review applications within 3-5 business days</li>
                                <li><strong>Receive Your Link:</strong> Get your unique referral link and marketing materials</li>
                                <li><strong>Promote:</strong> Share eSkrybe with your audience through blogs, videos, social media, etc.</li>
                                <li><strong>Earn:</strong> Receive monthly payments for all qualifying referrals</li>
                            </ol>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Who Should Apply</h2>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                                <li>Writing bloggers and content creators</li>
                                <li>Authors with an audience</li>
                                <li>Writing coaches and editors</li>
                                <li>Book reviewers</li>
                                <li>Writing community leaders</li>
                                <li>Podcasters covering writing or publishing</li>
                                <li>YouTubers in the writing niche</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Affiliate Guidelines</h2>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                                <li>Be honest and transparent about your affiliate relationship</li>
                                <li>Do not make false claims about eSkrybe features or pricing</li>
                                <li>Do not use paid ads that bid on "eSkrybe" branded terms</li>
                                <li>Do not spam or use unethical marketing practices</li>
                                <li>Comply with FTC disclosure requirements and local regulations</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Payment Details</h2>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                                <li>Minimum payout threshold: $50</li>
                                <li>Payment methods: PayPal or bank transfer</li>
                                <li>Payment schedule: Monthly, on the 15th of each month</li>
                                <li>30-day pending period for commission validation</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="font-serif text-xl font-semibold mb-3">Marketing Resources</h2>
                            <p className="text-muted-foreground">
                                Approved affiliates receive access to:
                            </p>
                            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                                <li>Banner ads in various sizes</li>
                                <li>Product screenshots and images</li>
                                <li>Email templates</li>
                                <li>Social media graphics</li>
                                <li>Feature comparison sheets</li>
                                <li>Demo videos</li>
                            </ul>
                        </section>
                    </div>
                </ScrollArea>

                <div className="mt-8 text-center">
                    <Button 
                        onClick={handleApplyClick} 
                        size="lg"
                        className="bg-accent hover:bg-accent/90 text-white"
                        data-testid="apply-affiliate-btn"
                    >
                        <Mail className="h-4 w-4 mr-2" />
                        Apply to Become an Affiliate
                    </Button>
                    <p className="text-sm text-muted-foreground mt-4">
                        Questions? Contact us at support@eskrybe.com
                    </p>
                </div>
            </div>
        </div>
    );
};
